import { useFeatureFlagsCRUDActions } from '@mm/state/actions/FeatureFlagsCRUDActions';
import { ENTER_KEY_NAME, TAB_KEY_NAME } from '@mm/constants/keys-codes';
import { invitesCRUDActions } from '@mm/state/actions/InvitesCRUDActions';
import subscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import UsersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import CompanyModel from '@mm/state/model/CompanyModel';
import CompanyUsersModel, {
  INVERTED_USER_ROLES,
  OldToNewRolesMap,
  UserRole,
} from '@mm/state/model/CompanyUsersModel';
import InviteModel from '@mm/state/model/InviteModel';
import {
  openInviteUserModal,
  openRemoveUserModal,
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import { selectActiveFacebookPageFromSession } from '@mm/state/selectors/facebookPageSelector';
import {
  activeCompanySelector,
  activeFacebookPageIdSelector,
  selectCompanies,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import { debounce, invert } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  compose,
  withHandlers,
  withPropsOnChange,
  withStateHandlers,
} from 'recompose';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import { PrimaryButton } from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import {
  Grid,
  GridCell,
  GridLabels,
  GridRow,
} from '../../../../common/components/Grid/Grid';
import { MiddleTrim } from '../../../../common/components/MiddleTrim/MiddleTrim';
import { SearchInput } from '../../../../common/components/SearchInput/SearchInput';
import * as routes from '../../../../common/constants/routes';
import { RouterComponentProps } from '../../../../common/helpers/routes/interfaces';
import { useResolutionInfo } from '@mm/hooks';
import './UsersPanel.pcss';
import { UserRoleContainer } from '../../../../common/hooks/use-route-guard';
import { DeleteOutlined, EditOutlined, TeamOutlined } from '@ant-design/icons';
import { SettingsSectionHeader } from '../common/SettingsSectionHeader/SettingsSectionHeader';
import { Pagination } from 'antd';

type OuterProps = {};

type StateAndHandlers = {
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
};

type Handlers = {
  getEditable: (user: CompanyUsersModel) => boolean;
  onChangePage: (pageNumber: number) => void;
  onKeyDownSearch: (e) => void;
  debouncedGetUsers: (
    companyId: number,
    page?: number,
    query?: string,
  ) => Promise<void>;
  onSearchInputChange: (e) => void;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  StateAndHandlers &
  Handlers &
  RouterComponentProps;

type Props = OuterProps & InnerProps;

const useFetchUsers = (props: Props) => {
  const { activeCompany, getUsers } = props;
  const activeCompanyId = activeCompany ? activeCompany.id : undefined;

  useEffect(() => {
    if (activeCompany) {
      getUsers(activeCompany.id);
    }
  }, [activeCompanyId]);
};

type RowProps = {
  className?: string;
  name?: string;
  email?: string;
  role?: string;
  date?: string;
  menu?: React.ReactNode | Element[];
  onNameCellClick?: () => void;
};

const RowTemplate = ({
  className,
  name,
  email,
  role = '',
  date,
  menu,
  onNameCellClick,
}: RowProps) => {
  const { isMobile } = useResolutionInfo();
  const maxLength = isMobile ? 30 : 20;
  const gridTemplateColumns =
    'minmax(50px, 1fr) minmax(50px, 2fr) minmax(50px, 1fr) minmax(50px, 1fr) minmax(50px, 0.5fr)';
  return (
    <GridRow
      className={classNames(className, 'users-panel__grid-body')}
      gridTemplateColumns={gridTemplateColumns}
    >
      <GridCell className="users-panel__table-name" onClick={onNameCellClick}>
        {name && <MiddleTrim maxLength={maxLength}>{name}</MiddleTrim>}
      </GridCell>
      <GridCell className="users-panel__email-cell">
        {email && <MiddleTrim maxLength={maxLength}>{email}</MiddleTrim>}
      </GridCell>
      <GridCell>{invert(OldToNewRolesMap)[role]}</GridCell>
      <GridCell>{moment(date).format('L')}</GridCell>
      <GridCell overflowable className="users-panel__actions-cell">
        {menu}
      </GridCell>
    </GridRow>
  );
};

const InviteRow = ({ invite }: { invite: InviteModel }) => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const cancelInvite = () => {
    dispatch(invitesCRUDActions.delete(invite));
  };

  return (
    <RowTemplate
      className="users-panel__invite-row"
      name="Invited User"
      email={invite.email || undefined}
      role={INVERTED_USER_ROLES[invite.role]}
      menu={
        <div>
          <DeleteOutlined onClick={cancelInvite} />
        </div>
      }
    />
  );
};

const UserRow = ({ user, openRemoveUserModal, activeCompany, getEditable }) => {
  const { firstName, lastName, email, role, createdAt, id } = user;
  const fullName = `${firstName} ${lastName}`;
  const { history } = useRouter();
  const { openUserEditModal } = useModalActions();

  const onEdit = () => {
    history.push(
      `${routes.COMPANY}/${activeCompany.id}${routes.USERS}/${id}/edit`,
    );
  };

  const onSelectItem = (value: string) => {
    if (value === 'edit') {
      openUserEditModal({ companyId: activeCompany.id, userId: id });
    }
    if (value === 'delete') {
      openRemoveUserModal(user, activeCompany);
    }
  };

  return (
    <RowTemplate
      name={fullName}
      email={email}
      role={role}
      date={createdAt}
      onNameCellClick={onEdit}
      menu={
        <div className="users-panel__actions">
          <EditOutlined onClick={() => onSelectItem('edit')} />{' '}
          <DeleteOutlined
            onClick={() => onSelectItem('delete')}
            disabled={!!getEditable(user)}
          />
        </div>
      }
    />
  );
};

const NoResultsFound = () => (
  <div className="users-panel__no-results-found">
    <h3 className="users-panel__no-results-headline">
      No matching users found
    </h3>
    <p>Please try again with a different search query</p>
  </div>
);

const UsersPanel = (props: Props) => {
  const {
    users,
    invites = [],
    openInviteUserModal,
    activeCompany,
    searchInput,
    onSearchInputChange,
    openRemoveUserModal,
    getEditable,
    usersPagination,
    onChangePage,
    onKeyDownSearch,
    fetchCompanyInvites,
  } = props;
  useFetchUsers(props);

  const activeCompanyId = activeCompany?.id;

  const isCompanyAdmin = useMemo(
    () => !!activeCompany?.editable,
    [activeCompany?.editable],
  );

  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const facebookPageFromSession = useSelector(
    selectActiveFacebookPageFromSession,
  );

  const fbPageId = activeFbPageId || facebookPageFromSession?.id;
  const gridTemplateColumns =
    'minmax(50px, 1fr) minmax(50px, 2fr) minmax(50px, 1fr) minmax(50px, 1fr) minmax(50px, 0.5fr)';

  const dispatch = useDispatch();
  const pagesWithAvailableSeats =
    useSelector((state: StoreState) => state.invite.pagesWithAvailableSeats) ||
    [];

  const areInvitesAllowed = isCompanyAdmin;
  const featureFlagsCRUDActions = useFeatureFlagsCRUDActions();
  const {
    roleFlags: { isFacebookPageCollaborator },
  } = UserRoleContainer.useContainer();

  useEffect(() => {
    if (fbPageId) {
      featureFlagsCRUDActions.retrieveSubscriptionFeatures(fbPageId);
    }
  }, [fbPageId]);

  useEffect(() => {
    dispatch(subscriptionCRUDActions.clearCurrentSubscription());
    if (activeCompanyId && areInvitesAllowed) {
      fetchCompanyInvites(activeCompanyId);
    }
  }, [areInvitesAllowed, activeCompanyId]);

  const cancelableInvites = invites.filter(
    (invite: InviteModel) => invite.active,
  );

  const onInviteClick = () => {
    if (activeCompany) {
      openInviteUserModal(activeCompany, pagesWithAvailableSeats);
    }
  };

  const hasUsers = !!users.length;

  return (
    <SettingsSectionHeader
      title="Users"
      color="#13C2C2"
      icon={<TeamOutlined />}
      headerComponent={
        <div className="users-panel__header">
          <SearchInput
            placeholder="Search User"
            className="users-panel__search-input"
            value={searchInput}
            onChange={onSearchInputChange}
            onKeyDown={onKeyDownSearch}
            hasBorder={false}
            useDarkStyle
          />
          {isCompanyAdmin && (
            <PrimaryButton
              onClick={onInviteClick}
              className="users-panel__primary-button"
            >
              Invite a user
            </PrimaryButton>
          )}
        </div>
      }
    >
      {isFacebookPageCollaborator && (
        <div className="settings__sidebar-link">
          Your role does not allow to preview this section
        </div>
      )}
      {!isFacebookPageCollaborator && (
        <Grid className="users-panel">
          <GridLabels
            className="users-panel__grid_cells"
            gridTemplateColumns={gridTemplateColumns}
          >
            <GridCell className="users-panel__cell">
              <span>Name</span>
            </GridCell>
            <GridCell className="users-panel__cell">
              <span>Email</span>
            </GridCell>
            <GridCell className="users-panel__cell">
              <span>Role</span>
            </GridCell>
            <GridCell className="users-panel__cell">
              <span>Created Date</span>
            </GridCell>
            <GridCell
              className={classNames(
                'users-panel__cell',
                'users-panel__actions-cell',
              )}
            >
              <span>Actions</span>
            </GridCell>
          </GridLabels>

          {areInvitesAllowed &&
            cancelableInvites.map(invite => (
              <InviteRow key={invite.id} invite={invite} />
            ))}

          {hasUsers &&
            users.map(user => (
              <UserRow
                key={user.id}
                user={user}
                openRemoveUserModal={openRemoveUserModal}
                activeCompany={activeCompany}
                getEditable={getEditable}
              />
            ))}
          {hasUsers && (
            <Pagination
              defaultPageSize={25}
              hideOnSinglePage
              total={usersPagination.total}
              onChange={onChangePage}
              showSizeChanger={false}
              className="users-panel__pagination"
            />
          )}
          {!hasUsers && <NoResultsFound />}
        </Grid>
      )}
    </SettingsSectionHeader>
  );
};

const initialState = {
  searchInput: '',
};

const stateUpdaters = {
  setSearchInput: () => (value: string) => ({ searchInput: value }),
};

const mapStateToProps = (state: StoreState) => ({
  invites: state.invite.entities,
  activeCompany: activeCompanySelector(state),
  companies: selectCompanies(state),
  companyUsers: state.companyUsers.entities,
  loggedOut: state.session.loggedOut,
  session: state.session.session,
  users: state.companyUsers.entities,
  usersPagination: state.companyUsers.pagination,
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getUsers: (companyId: number, page?: number, query?: string) =>
    dispatch(UsersCRUDActions.retrieveAll(companyId, page, 25, query)),
  openInviteUserModal: (company: CompanyModel, availableSeats: number[]) =>
    dispatch(openInviteUserModal(company, availableSeats)),
  openRemoveUserModal: (user: CompanyUsersModel, company: CompanyModel) =>
    dispatch(openRemoveUserModal(user, company)),
  fetchCompanyInvites: (companyId: number) =>
    dispatch(invitesCRUDActions.retrieveAll(companyId)),
});

const onSearchInputChange =
  ({ searchInput, setSearchInput, debouncedGetUsers, activeCompany }) =>
  e => {
    const usersSearchFilter =
      e.target.value.length === 1 ? e.target.value.trim() : e.target.value;
    if (searchInput !== usersSearchFilter) {
      debouncedGetUsers(activeCompany.id, 1, usersSearchFilter);
    }
    setSearchInput(usersSearchFilter);
  };

const onKeyDownSearch =
  ({ activeCompany, getUsers }: Props) =>
  e => {
    const usersSearchFilter = e.target.value.trim();
    if (e.key === ENTER_KEY_NAME || e.key === TAB_KEY_NAME) {
      e.preventDefault();

      if (activeCompany) {
        getUsers(activeCompany.id, 1, usersSearchFilter);
      }
    }
  };

const getEditable =
  ({ activeCompany, session }: Props) =>
  (user: CompanyUsersModel) => {
    const sameUser = session && session.id === user.id;
    const owner = user && user.role === UserRole.Owner;
    return activeCompany && activeCompany.editable && !sameUser && !owner;
  };

const onChangePage =
  ({ getUsers, activeCompany, searchInput }: Props) =>
  (pageNumber: number) => {
    if (activeCompany) {
      getUsers(activeCompany.id as number, pageNumber, searchInput);
    }
  };

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(initialState, stateUpdaters),
  withHandlers({
    getEditable,
    onChangePage,
    onKeyDownSearch,
  }),
  // Helps requesting BE not too often.
  withPropsOnChange(['getUsers'], ({ getUsers }) => ({
    debouncedGetUsers: debounce(getUsers, 1000),
  })),
  withHandlers({
    onSearchInputChange,
  }),
);

export default enhance(UsersPanel);
